import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { getFhirIdFromEntity } from 'src/utils/fhir';
import { convertValueToValueSet } from '../common-utils';
import { WrappedOrganization } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import { WrappedValueSet } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { isEmpty } from 'lodash';
import { FhirResource, Reference } from 'src/careflow/ph-core/fhir-client/resources';
import { WrappedDocumentReference } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/DocumentReference';

export type ValueKey = 'valueString' | 'valueReference' | 'valueUrl';

export const VALUE_KEYS: ValueKey[] = ['valueString', 'valueReference', 'valueUrl'];

export interface SelectAttachmentState {
  openUploadFile: boolean;
  index: number | null;
  selectedDocumentReference: WrappedDocumentReference | null;
}

const convertBlobToBase64 = (file: Blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      resolve(reader.result?.toString().split(',')[1]);
    };
    reader.readAsDataURL(file);
  });

export const createBinaryCustom = async (
  file: any,
  patient: WrappedPatient | null,
  handleCreateBynary: Function
): Promise<any> => {
  if (!file?.file) return null;

  const resourceType = 'Binary';
  const contentType = file?.fileType;
  const data = await convertBlobToBase64(file?.file);
  const securityContext = { reference: `${patient?.resourceType}/${patient?.id}` };
  const { name } = file;
  const obj = { resourceType, contentType, securityContext, data };
  const binary = await handleCreateBynary(obj);
  const objNew = { ...binary[0], name: name };
  return objNew;
};

export const saveDocumentReference = (
  binary: any,
  data: any,
  authUser: any,
  organizations: WrappedOrganization[] | null,
  documentCategory: WrappedValueSet | null,
  documentType: WrappedValueSet | null,
  patient: WrappedPatient | null,
  resource: FhirResource | undefined,
  configurations: any,
  encounter?: Reference
) => {
  const codeValueCategory = data?.category
    ? data.category?.map((e) => {
        let valueSet = convertValueToValueSet(e, documentCategory);

        if (!valueSet) {
          valueSet = { code: e, display: e };
        }

        return {
          coding: [{ ...valueSet }],
          text: e,
        };
      })
    : [];

  const dataCustodian = data?.custodian || getFhirIdFromEntity(authUser.organization_fhir_uri);

  const newCustodian = organizations
    ?.filter((e) => e.id === dataCustodian)
    .map((t) => {
      const reference = `Organization/${t.id}`;
      const display = t.name;
      return { reference, display };
    });

  const codeValueType = data?.type ? convertValueToValueSet(data.type, documentType) : {};

  let documentReference: any = {
    resourceType: 'DocumentReference',
    status: 'current',
    subject: { reference: `${patient?.resourceType}/${patient?.id}` },
    ...(!isEmpty(codeValueCategory) && { category: [...codeValueCategory] }),
    ...(!isEmpty(codeValueType) && { type: { coding: [{ ...codeValueType }] } }),
    author: [
      {
        ...(!isEmpty(authUser.user_fhir_uri.trim()) && {
          reference: `Practitioner/${getFhirIdFromEntity(authUser.user_fhir_uri)}`,
        }),
        display: authUser.name,
      },
    ],
    ...(newCustodian?.[0] && {
      custodian: newCustodian[0],
    }),
    date: new Date().toISOString(),
    content: [
      {
        attachment: {
          contentType: `${binary?.contentType}`,
          creation: new Date().toISOString(),
          title: binary.name,
          url: `${configurations?.baseUrl}/Binary/${binary?.id}`,
        },
      },
    ],
    ...(resource && {
      context: {
        related: [{ reference: `${resource?.resourceType}/${resource?.id}` }],
      },
    }),
  };

  if (encounter) {
    documentReference = {
      ...documentReference,
      context: {
        ...(documentReference.context ?? {}),
        encounter: [...(documentReference.context?.encounter ?? []), encounter],
      },
    };
  }

  return documentReference;
};

export const mapDocumentReferenceToResource = (
  documentReference: WrappedDocumentReference[],
  resource: any
) => {
  const document = documentReference?.map((doc) => `${doc?.resourceType}/${doc?.id}`);
  const supportingInfo = resource?.supportingInfo?.map((sd) => `${sd?.reference}`) ?? [];
  const setDocument = [...new Set([...supportingInfo, ...document])];
  const allDocuments = setDocument?.map((x3) => ({ reference: x3 }));
  return allDocuments;
};

export const canViewDocument = (inputTypeFiles, inputType, inputValue) => {
  const config = inputTypeFiles[inputType];
  const hasValue = Boolean(inputValue);
  const isDocumentReference =
    typeof inputValue === 'string' && inputValue.includes('DocumentReference');

  const isUrl =
    typeof inputValue === 'string' &&
    (inputValue.startsWith('http://') || inputValue.startsWith('https://'));

  const canPreviewDocumentReference = hasValue && isDocumentReference;
  const canPreviewUrl = hasValue && isUrl;
  const canUpload = config?.isFile && !hasValue;
  const isFile = isDocumentReference || config?.isFile;
  return { canPreviewDocumentReference, canPreviewUrl, canUpload, isFile };
};
